import { useT } from "../../../../shared/i18n/i18n";
import { FormEvent, useEffect, useState } from "react";
import type {
  CreateWorkObjectTypeInput,
  WorkObjectTypeCategory,
  WorkObjectTypeDefinition,
  WorkObjectTypeIcon,
} from "../../../../entities/work-object-type/model/use-work-object-types";
import { normalizeWorkObjectTypeIcon } from "../../../../entities/work-object-type/model/work-object-type-icons";
import { slugifyWorkObjectTypeKey } from "../../../../entities/work-object-type/model/work-object-type-key";
import { validateWorkObjectTypeForm } from "../model/work-object-type-form-schema";
import { WorkObjectTypeCategoryPicker } from "./work-object-type-category-picker";
import { WorkObjectTypeIconPicker } from "./work-object-type-icon-picker";
import { EditableText } from "@/shared/ui/editable-text";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";

type WorkObjectTypeDialogProps = {
  categories: WorkObjectTypeCategory[];
  existingTypes: WorkObjectTypeDefinition[];
  isArchiving: boolean;
  isSaving: boolean;
  onArchiveCategory: (categoryId: string) => Promise<void>;
  onArchive: (typeId: string) => Promise<void>;
  onCreateCategory: (name: string) => Promise<WorkObjectTypeCategory>;
  onOpenChange: (open: boolean) => void;
  onRenameCategory: (input: { id: string; name: string }) => Promise<WorkObjectTypeCategory>;
  onSave: (input: CreateWorkObjectTypeInput, type: WorkObjectTypeDefinition | null) => Promise<void>;
  open: boolean;
  type: WorkObjectTypeDefinition | null;
};

export function WorkObjectTypeDialog({
  categories,
  existingTypes,
  isArchiving,
  isSaving,
  onArchiveCategory,
  onArchive,
  onCreateCategory,
  onOpenChange,
  onRenameCategory,
  onSave,
  open,
  type,
}: WorkObjectTypeDialogProps) {
  const t = useT();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [icon, setIcon] = useState<WorkObjectTypeIcon>("circle");
  const [isKeyDirty, setIsKeyDirty] = useState(false);
  const [isKeyTouched, setIsKeyTouched] = useState(false);
  const [isNameTouched, setIsNameTouched] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [key, setKey] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    const nextCategoryId = type?.categoryId ?? null;
    setCategoryId(nextCategoryId);
    setError(null);
    setIcon(normalizeWorkObjectTypeIcon(type?.icon ?? "circle"));
    setIsKeyDirty(Boolean(type));
    setIsKeyTouched(false);
    setIsNameTouched(false);
    setIsSubmitAttempted(false);
    setKey(type?.key ?? "");
    setName(type?.name ?? "");
  }, [type, open]);

  const isExisting = type !== null;
  const canRename = !isExisting || type.canRename;
  const validation = validateWorkObjectTypeForm({ categoryId, icon, key, name }, existingTypes, type?.id ?? null);
  const shouldShowNameError = isNameTouched || isSubmitAttempted;
  const shouldShowKeyError = isKeyTouched || isSubmitAttempted;
  const nameError = shouldShowNameError ? validation.errors.name ?? null : null;
  const keyError = shouldShowKeyError ? validation.errors.key ?? null : null;
  const canSave = validation.success && canRename;

  function updateName(nextName: string) {
    setName(nextName);
    if (!isKeyDirty) {
      setKey(slugifyWorkObjectTypeKey(nextName));
    }
  }

  function updateKey(nextKey: string) {
    setIsKeyDirty(true);
    setKey(slugifyWorkObjectTypeKey(nextKey));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setIsSubmitAttempted(true);
    const nextValidation = validateWorkObjectTypeForm({ categoryId, icon, key, name }, existingTypes, type?.id ?? null);
    if (!nextValidation.success || !canRename) {
      return;
    }

    setError(null);
    try {
      await onSave(
        {
          categoryId: nextValidation.data.categoryId,
          icon: normalizeWorkObjectTypeIcon(nextValidation.data.icon),
          key: nextValidation.data.key,
          name: nextValidation.data.name,
        },
        type,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save type.");
    }
  }

  async function archive() {
    if (!type) {
      return;
    }

    setError(null);
    try {
      await onArchive(type.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive type.");
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-xl gap-5 p-0">
        <DialogHeader className="px-8 pt-8 pr-12">
          <DialogTitle>{isExisting ? t("typeDialog.settings") : t("typeDialog.newType")}</DialogTitle>
          <DialogDescription>{t("itemTypes.whatItIs")}</DialogDescription>
        </DialogHeader>

        <form className="flex flex-col gap-4 px-8 pb-8" onSubmit={submit}>
          <div className="flex items-start gap-3">
            <WorkObjectTypeIconPicker compact disabled={!canRename || isSaving} onValueChange={setIcon} value={icon} />
            <div className="min-w-0 flex-1 pt-1.5">
              {canRename ? (
                <EditableText
                  ariaLabel={t("typeDialog.typeName")}
                  className="min-w-0"
                  inputClassName="text-base font-semibold leading-6"
                  onChange={(nextName) => {
                    setIsNameTouched(true);
                    updateName(nextName);
                  }}
                  onInputChange={updateName}
                  placeholder={t("itemTypes.untitled")}
                  previewClassName="text-base font-semibold leading-6"
                  selectOnFocus={!isExisting}
                  value={name}
                />
              ) : (
                <p className="truncate text-base font-semibold leading-6">{name}</p>
              )}
              {nameError ? <FieldError className="mt-2">{nameError}</FieldError> : null}
            </div>
          </div>

          <label className="grid gap-1.5 text-sm font-medium">
            {t("itemTypes.key")}
            <Input
              className="h-8"
              disabled={!canRename || isSaving}
              onChange={(event) => updateKey(event.target.value)}
              onBlur={() => setIsKeyTouched(true)}
              value={key}
            />
            {keyError ? <FieldError>{keyError}</FieldError> : null}
          </label>

          <div className="grid gap-1.5">
            <p className="text-sm font-medium">{t("itemTypes.category")}</p>
            <WorkObjectTypeCategoryPicker
              categories={categories}
              disabled={isSaving}
              onArchive={onArchiveCategory}
              onCreate={onCreateCategory}
              onRename={onRenameCategory}
              onValueChange={setCategoryId}
              value={categoryId}
            />
          </div>

          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex items-center justify-between gap-2 pt-4">
            {type?.canDelete ? (
              <AlertDialog>
                <AlertDialogTrigger render={<Button type="button" variant="ghost" />}>{t("itemTypes.archive")}</AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("itemTypes.archiveConfirm")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("itemTypes.archiveHint")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction disabled={isArchiving} onClick={archive} variant="destructive">
                      {isArchiving ? t("typeDialog.archiving") : t("typeDialog.archiveType")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
                {t("common.cancel")}
              </Button>
              <Button disabled={!canSave || isSaving} type="submit">
                {isSaving ? t("fieldDialog.saving") : isExisting ? t("typeDialog.saveChanges") : t("typeDialog.createType")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
